import { Alert, AlertIcon, Button, Flex, Input, Text } from '@chakra-ui/react'
import { useApp } from '@hb/react'
import { useState } from 'react'
import { updateAssessmentDeliveryStatus } from '../../../../backend/functions/other'
import { useProfile } from '../../../../contexts'
import { useLaborCharting } from '../context'

interface TimeOfBirthStepProps {
  onSaved: (timeOfBirth: number) => void
}

const toDatetimeLocalValue = (ms: number): string => {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

export const TimeOfBirthStep = ({ onSaved }: TimeOfBirthStepProps) => {
  const { appName } = useApp()
  const { assessmentId } = useProfile()
  const { saveMinimumDeliveryReport } = useLaborCharting()
  const [birthValue, setBirthValue] = useState(() => toDatetimeLocalValue(Date.now()))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!assessmentId) return
    const timeOfBirth = new Date(birthValue).getTime()
    if (!timeOfBirth || isNaN(timeOfBirth)) {
      setError('Please enter a valid time of birth.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      // §4.1.1: Create initial delivery record — turns patient status purple.
      await updateAssessmentDeliveryStatus({
        appName,
        assessmentId,
        deliveredOn: timeOfBirth,
      })
      // Write minimum delivery report with timeOfBirth so step 2 can read it.
      await saveMinimumDeliveryReport({ timeOfBirth })
      onSaved(timeOfBirth)
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save time of birth. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Flex direction="column" gap={5} px={6} py={6}>
      {/* Compliance: provider-entered time of birth */}
      <Alert status="info" borderRadius={6} fontSize="sm" py={2.5}>
        <AlertIcon />
        <Text fontSize="sm">
          Enter the actual time of birth. This is a legal record used for the birth certificate and
          delivery claim.
        </Text>
      </Alert>

      <Flex direction="column" gap={2}>
        <Text
          fontFamily="Hero-New"
          fontWeight={700}
          fontSize="lg"
          color="gray.700"
          _dark={{ color: 'gray.200' }}>
          Time of Birth
        </Text>
        <Input
          type="datetime-local"
          value={birthValue}
          onChange={e => setBirthValue(e.target.value)}
          size="lg"
          fontFamily="Hero-New"
          fontSize="lg"
          isDisabled={saving}
        />
      </Flex>

      {error && (
        <Alert status="error" borderRadius={6} py={2} fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Button
        colorScheme="pink"
        fontFamily="Hero-New"
        fontWeight={700}
        size="lg"
        isLoading={saving}
        isDisabled={!birthValue}
        onClick={handleSave}>
        Save Time of Birth
      </Button>
    </Flex>
  )
}
